import config from '../../config';
import { createWebSocketTicket } from './createTicket';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export interface WebSocketSubscriber {
    /** Every frame the socket receives; each subscriber picks out its own events. */
    onMessage: (data: string) => void;
    /**
     * Every open, including reconnects, and immediately for a subscriber that
     * joins an already-open socket. It is the cue to reload from the API: a
     * socket only carries what happens while it is up, so whatever it missed
     * has to be fetched.
     */
    onConnected?: () => void;
}

/**
 * The one connection to the server.
 *
 * The backend pushes every realtime event - device online/offline, logs sync
 * progress - down a single socket to every client, so the browser needs exactly
 * one of them, not one per feature. A second connection would burn a second
 * ticket, double the fan-out on the server and still receive the same frames.
 *
 * The socket is opened by the first subscriber and closed after the last one
 * leaves, so nothing stays open behind a closed dialog.
 */
const subscribers = new Set<WebSocketSubscriber>();

let socket: WebSocket | null = null;
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
let connecting = false;
let connected = false;
let attempt = 0;
/** Bumped whenever the socket is torn down, so an in-flight connect can tell it was superseded. */
let generation = 0;

const buildAuthenticatedWebSocketUrl = (wsBaseUrl: string, wsToken: string) => {
    const url = new URL(wsBaseUrl);
    url.searchParams.set('ws_token', wsToken);

    return url.toString();
};

const notify = (deliver: (subscriber: WebSocketSubscriber) => void) => {
    // Snapshot first: a subscriber is free to unsubscribe while being told.
    Array.from(subscribers).forEach((subscriber) => {
        try {
            deliver(subscriber);
        } catch (error) {
            console.error('WebSocket subscriber failed.', error);
        }
    });
};

const detachSocket = () => {
    if (!socket) {
        return;
    }

    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
    }

    socket = null;
};

const stop = () => {
    generation += 1;
    connecting = false;
    connected = false;
    attempt = 0;

    if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
    }

    detachSocket();
};

const scheduleReconnect = () => {
    if (subscribers.size === 0 || reconnectTimeoutId) {
        return;
    }

    // Backoff with jitter: every open tab would otherwise retry in lockstep and
    // hammer the API the moment it blips.
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
    attempt += 1;

    reconnectTimeoutId = setTimeout(() => {
        reconnectTimeoutId = null;
        connect();
    }, delay * (0.5 + Math.random() * 0.5));
};

const connect = async () => {
    const wsBaseUrl = config.WEBSOCKET_URL;

    if (!wsBaseUrl) {
        console.warn('REACT_APP_WEBSOCKET_URL is not configured. Realtime updates are disabled.');
        return;
    }

    connecting = true;

    const startedAt = generation;
    const isCurrent = () => startedAt === generation && subscribers.size > 0;

    let wsToken: string | null = null;

    try {
        const ticket = await createWebSocketTicket();
        wsToken = ticket?.ws_token ?? null;
    } catch (error) {
        console.error('Failed to issue websocket ticket.', error);
    }

    if (!isCurrent()) {
        // Superseded while the ticket was in flight; stop() already reset the flags.
        return;
    }

    connecting = false;

    if (!wsToken) {
        scheduleReconnect();
        return;
    }

    try {
        socket = new WebSocket(buildAuthenticatedWebSocketUrl(wsBaseUrl, wsToken));
    } catch (error) {
        console.error('Failed to open websocket.', error);
        socket = null;
        scheduleReconnect();
        return;
    }

    socket.onopen = () => {
        if (!isCurrent()) {
            return;
        }

        attempt = 0;
        connected = true;
        notify((subscriber) => subscriber.onConnected?.());
    };

    socket.onmessage = (event) => {
        if (!isCurrent()) {
            return;
        }

        notify((subscriber) => subscriber.onMessage(event.data as string));
    };

    socket.onerror = (event) => {
        console.error('WebSocket error.', event);
    };

    socket.onclose = () => {
        if (!isCurrent()) {
            return;
        }

        connected = false;
        socket = null;
        scheduleReconnect();
    };
};

const ensureConnected = () => {
    if (connecting || socket || reconnectTimeoutId) {
        return;
    }

    connect();
};

/** Subscribe to the shared socket. Returns the unsubscribe. */
export const subscribeToWebSocket = (subscriber: WebSocketSubscriber): (() => void) => {
    subscribers.add(subscriber);

    if (connected) {
        // Joining a socket that is already up still needs the reload cue.
        try {
            subscriber.onConnected?.();
        } catch (error) {
            console.error('WebSocket subscriber failed.', error);
        }
    } else {
        ensureConnected();
    }

    return () => {
        subscribers.delete(subscriber);

        if (subscribers.size === 0) {
            stop();
        }
    };
};

/** Test seam: drops every subscriber and the socket with them. */
export const resetWebSocket = () => {
    subscribers.clear();
    stop();
};
